import { ComponentChildren } from "preact"
import { useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { FileText, X } from "lucide-react"
import { selected_file_label, upload_error_message } from "@/components/report_upload_panel"

/** One named file field in the multipart POST, e.g. Kroger's `export_grid`. */
export interface ReportFileSlot {
  /** The multipart field name the server reads. */
  field: string
  title: string
  description?: string
  /** react-dropzone `accept` map. */
  accept: Record<string, string[]>
  /** Several files under the same field (Kroger's backup zips). */
  multiple?: boolean
  /** Upload stays disabled until every required slot has a file. */
  required?: boolean
}

interface MultiReportUploadPanelProps<T> {
  endpoint: string
  /** Extra form fields sent with the files, e.g. `{ integration_id }`. */
  fields?: Record<string, string>
  slots: ReportFileSlot[]
  /** Refuse a selection whose files add up to more than this, before posting it. */
  maxTotalBytes?: number
  successMessage: (response: T) => { title: string; description: string }
  onUploaded?: (response: T) => void
  /** How to get the reports — rendered above the file slots. */
  children?: ComponentChildren
  className?: string
}

export type SlotSelection = Record<string, File[]>

/** Whether every required slot has a file. */
export function missing_required_slots(slots: ReportFileSlot[], selection: SlotSelection): string[] {
  return slots
    .filter((slot: ReportFileSlot) => slot.required && (selection[slot.field] ?? []).length === 0)
    .map((slot: ReportFileSlot) => slot.title)
}

export function total_selected_bytes(selection: SlotSelection): number {
  return Object.values(selection)
    .flat()
    .reduce((total: number, file: File) => total + file.size, 0)
}

function format_megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Upload a retailer report that comes as several files (Kroger's three PartnerPass CSVs plus
 * backup zips, Hy-Vee's billback and SAP exports). The single-file case is `ReportUploadPanel`.
 */
export function MultiReportUploadPanel<T = unknown>({
  endpoint,
  fields,
  slots,
  maxTotalBytes,
  successMessage,
  onUploaded,
  children,
  className,
}: MultiReportUploadPanelProps<T>) {
  const [selection, setSelection] = useState<SlotSelection>({})
  const [uploading, setUploading] = useState(false)
  const [uploaded, setUploaded] = useState(false)

  const missing = missing_required_slots(slots, selection)
  const totalBytes = total_selected_bytes(selection)
  const oversize = maxTotalBytes !== undefined && totalBytes > maxTotalBytes
  const canUpload = missing.length === 0 && !oversize && !uploading

  function setSlotFiles(field: string, files: File[]) {
    setSelection((current: SlotSelection) => ({ ...current, [field]: files }))
    setUploaded(false)
  }

  async function handleUpload() {
    if (!canUpload) return
    setUploading(true)
    try {
      const formData = new FormData()
      Object.entries(fields ?? {}).forEach(([name, value]: [string, string]) => formData.append(name, value))
      for (const slot of slots) {
        for (const file of selection[slot.field] ?? []) {
          formData.append(slot.field, file)
        }
      }

      const response = await fetch(endpoint, { method: "POST", credentials: "same-origin", body: formData })
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(upload_error_message(errorText, response.status))
      }
      const data = (await response.json()) as T

      setSelection({})
      setUploaded(true)
      toast(successMessage(data))
      onUploaded?.(data)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed"
      toast({ title: "Upload failed", description: message, variant: "destructive" })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      {children}

      <div class="space-y-3">
        {slots.map((slot: ReportFileSlot, index: number) => (
          <FileSlotRow
            key={slot.field}
            step={index + 1}
            slot={slot}
            files={selection[slot.field] ?? []}
            disabled={uploading}
            onChange={(files: File[]) => setSlotFiles(slot.field, files)}
          />
        ))}
      </div>

      {oversize && maxTotalBytes !== undefined && (
        <p class="text-sm text-red-600">
          These files add up to {format_megabytes(totalBytes)}, over the {format_megabytes(maxTotalBytes)} limit. Upload
          fewer backup zips at a time.
        </p>
      )}

      <div class="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => setSelection({})} disabled={totalBytes === 0 || uploading}>
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
        <Button onClick={handleUpload} disabled={!canUpload}>
          {uploading ? "Uploading..." : "Upload & Start Sync"}
        </Button>
      </div>

      {uploaded && (
        <p class="text-sm text-green-700">Sync started. It runs in the background and usually takes a few minutes.</p>
      )}
    </div>
  )
}

interface FileSlotRowProps {
  step: number
  slot: ReportFileSlot
  files: File[]
  disabled: boolean
  onChange: (files: File[]) => void
}

function FileSlotRow({ step, slot, files, disabled, onChange }: FileSlotRowProps) {
  function handleDrop(dropped: File[]) {
    onChange(slot.multiple ? [...files, ...dropped] : dropped.slice(0, 1))
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    multiple: slot.multiple ?? false,
    noClick: true,
    noKeyboard: true,
    accept: slot.accept,
  })

  return (
    <div
      {...(getRootProps() as object)}
      class={`rounded-md border p-3 transition-colors ${
        isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-200"
      }`}>
      <input {...getInputProps()} />
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="text-sm font-medium text-gray-900">
            {step}. {slot.title}
            {!slot.required && <span class="ml-1 font-normal text-gray-500">(optional)</span>}
          </p>
          {slot.description && <p class="text-xs text-gray-500 mt-0.5">{slot.description}</p>}
        </div>
        <div class="flex items-center gap-2 shrink-0">
          {files.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => onChange([])} disabled={disabled}>
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={open} disabled={disabled}>
            {slot.multiple ? "Add files" : "Select file"}
          </Button>
        </div>
      </div>
      <div class="mt-2 space-y-1">
        {files.length === 0 ? (
          <p class="flex items-center gap-2 text-xs text-gray-400">
            <FileText className="h-3.5 w-3.5 shrink-0" />
            {isDragActive ? "Drop file here" : selected_file_label(null)}
          </p>
        ) : (
          files.map((file: File, index: number) => (
            <p key={`${file.name}-${index}`} class="flex items-center gap-2 text-xs text-gray-700 min-w-0">
              <FileText className="h-3.5 w-3.5 text-gray-500 shrink-0" />
              <span class="truncate">{selected_file_label(file)}</span>
            </p>
          ))
        )}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const slots: ReportFileSlot[] = [
    { field: "billbacks_file", title: "Billbacks", accept: {}, required: true },
    { field: "sap_file", title: "SAP", accept: {}, required: true },
    { field: "pdf_backups", title: "Backups", accept: {}, multiple: true },
  ]

  describe("missing_required_slots", () => {
    it("names each required slot without a file and ignores optional ones", () => {
      expect(missing_required_slots(slots, {})).toEqual(["Billbacks", "SAP"])
      expect(missing_required_slots(slots, { billbacks_file: [new File(["a"], "b.csv")], sap_file: [] })).toEqual([
        "SAP",
      ])
      expect(
        missing_required_slots(slots, {
          billbacks_file: [new File(["a"], "b.csv")],
          sap_file: [new File(["a"], "s.csv")],
        })
      ).toEqual([])
    })
  })

  describe("total_selected_bytes", () => {
    it("adds every file in every slot", () => {
      expect(total_selected_bytes({})).toBe(0)
      expect(
        total_selected_bytes({
          billbacks_file: [new File([new Uint8Array(100)], "b.csv")],
          pdf_backups: [new File([new Uint8Array(30)], "1.zip"), new File([new Uint8Array(20)], "2.zip")],
        })
      ).toBe(150)
    })
  })
}
